/** Fire-and-forget UI sound effects for the till, via the Web Audio API.
 *
 * Files live in `public/sounds` (served from `/sounds`). They are WAV/PCM (`.wav`): the Badger
 * app's cues ship as Opus-in-Ogg, and while AAC `.m4a` plays in an `<audio>` element on the LSK
 * iPad, that WKWebView's `decodeAudioData` REJECTS AAC. PCM/WAV needs no codec to decode, so it is
 * the reliable input for Web Audio across WKWebView, Safari, Chrome, and Firefox.
 *
 * Why Web Audio and not `<audio>`: iOS/WKWebView blocks programmatic playback that isn't tied to a
 * user gesture. With `<audio>` elements the scan cue (fired from the QR-decode callback, not a tap)
 * stayed silent, while the unlock blip leaked sound on the opening tap. With Web Audio we instead
 * resume a single `AudioContext` on the first gesture (silent — no playback); once it is running,
 * iOS allows `BufferSource.start()` from any later callback. Buffers are fetched + decoded eagerly
 * at startup (decoding needs no gesture), so a cue is ready by the time the first QR is scanned.
 *
 * Playback is strictly best-effort: a missing context, a failed decode, or a missing file must
 * NEVER interrupt the scan/redeem flow, so {@link playSound} swallows every error. (Use
 * {@link playSoundDebug} when you want the failure reason — debug panel only.)
 */
const FILES = {
  scan: 'badger_scan', // consumer identified by a fresh scan
  reward: 'badger_reward', // a reward was redeemed
  complete: 'badger_complete', // reserved: stamp card completed
} as const

export type SoundName = keyof typeof FILES

let ctx: AudioContext | null = null
const buffers = new Map<SoundName, AudioBuffer>()
let loadStarted = false

/** Lazily create the shared AudioContext. Created suspended before a gesture (decoding still works);
 * {@link installAudioUnlock} resumes it on the first tap. Returns null if Web Audio is unavailable. */
function getCtx(): AudioContext | null {
  if (ctx) return ctx
  if (typeof window === 'undefined') return null
  const Ctor =
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) return null
  try {
    ctx = new Ctor()
  } catch {
    return null
  }
  return ctx
}

async function load(name: SoundName): Promise<void> {
  const c = getCtx()
  if (!c || buffers.has(name)) return
  const res = await fetch(`${import.meta.env.BASE_URL}sounds/${FILES[name]}.wav`)
  if (!res.ok) throw new Error(`fetch ${res.status}`)
  const bytes = await res.arrayBuffer()
  // Safari's decodeAudioData is callback-only on older versions; wrap so both shapes resolve. Its
  // error callback can fire with a null/empty arg, so normalise to a real Error (never bare "null").
  const buf = await new Promise<AudioBuffer>((resolve, reject) => {
    const fail = (e: unknown) =>
      reject(e instanceof Error ? e : new Error(`decodeAudioData failed${e ? `: ${String(e)}` : ''}`))
    const p = c.decodeAudioData(bytes, resolve, fail)
    if (p && typeof p.then === 'function') p.then(resolve, fail)
  })
  buffers.set(name, buf)
}

/** Begin fetching + decoding every cue. Decoding needs no user gesture, so run it at startup. */
function loadAll(): void {
  if (loadStarted) return
  loadStarted = true
  for (const name of Object.keys(FILES) as SoundName[]) void load(name).catch(() => {})
}

function emit(c: AudioContext, buf: AudioBuffer): void {
  const src = c.createBufferSource()
  src.buffer = buf
  src.connect(c.destination)
  src.start()
}

/** Play a UI sound. Never throws — failures are silently ignored.
 *
 * Starting the camera capture (getUserMedia) suspends the AudioContext on iOS, so by the time a QR
 * is decoded the context is often suspended even though it was resumed on the opening tap. Resume
 * first, THEN emit (in the resume callback) — emitting onto a suspended context is silent. */
export function playSound(name: SoundName): void {
  try {
    const c = getCtx()
    const buf = buffers.get(name)
    if (!c || !buf) return
    if (c.state === 'suspended') {
      void c.resume().then(() => emit(c, buf)).catch(() => {})
    } else {
      emit(c, buf)
    }
  } catch {
    /* best effort — sound is non-essential */
  }
}

/** iOS/WKWebView starts the AudioContext suspended until a user gesture. Our cues fire from the
 * scan-decode and redeem callbacks (not direct taps), so we resume the context on the first gesture
 * anywhere in the app — silently, with no playback. Idempotent; listeners self-remove after the
 * first gesture. Also kicks off buffer decoding (independent of any gesture). */
export function installAudioUnlock(): void {
  if (typeof window === 'undefined') return
  loadAll()
  const events = ['pointerdown', 'touchstart', 'keydown'] as const

  function remove() {
    for (const ev of events) window.removeEventListener(ev, unlock)
  }

  function unlock() {
    const c = getCtx()
    if (c && c.state === 'suspended') void c.resume()
    remove()
  }

  for (const ev of events) window.addEventListener(ev, unlock, { passive: true })
}

/** Like {@link playSound} but reports the outcome — for the debug panel, so an operator can tell a
 * decode failure from a suspended context on a real device. Resolves to `null` on success, or a
 * short `name: message` string on failure. */
export async function playSoundDebug(name: SoundName): Promise<string | null> {
  try {
    const c = getCtx()
    if (!c) return 'no AudioContext (Web Audio unavailable)'
    if (c.state === 'suspended') await c.resume()
    if (!buffers.has(name)) await load(name)
    const buf = buffers.get(name)
    if (!buf) return 'decode failed (no buffer)'
    emit(c, buf)
    return null
  } catch (e) {
    return e instanceof Error ? `${e.name}: ${e.message}` : String(e)
  }
}
